/**
 * Form configurations for auth components
 */

export const FORM_CONFIGS = {
  login: {
    title: 'auth.login.welcome', // i18n key
    subtitle: 'auth.login.subtitle', // i18n key
    scope: 'form-1',
    fields: [
      {
        name: 'email',
        type: 'email',
        label: 'auth.email_placeholder', // i18n key
        validation: 'required|email|no_spaces',
        dataQa: 'auth-email',
        autocomplete: 'username'
      },
      {
        name: 'password',
        type: 'password',
        label: 'auth.password_placeholder', // i18n key
        validation: 'required|no_spaces',
        dataQa: 'auth-password',
        autocomplete: 'current-password'
      }
    ],
    submitText: 'auth.login.button', // i18n key
    submitQa: 'auth-submit'
  },

  signup: {
    title: 'auth.signup.welcome', // i18n key
    subtitle: 'auth.signup.subtitle', // i18n key
    scope: 'form-2',
    fields: [
      {
        name: 'firstname',
        type: 'text',
        label: 'auth.firstname', // i18n key
        validation: 'required',
        wrapperClass: 'name-input',
        autocomplete: 'given-name'
      },
      {
        name: 'lastname',
        type: 'text',
        label: 'auth.lastname', // i18n key
        validation: 'required',
        wrapperClass: 'name-input',
        autocomplete: 'family-name'
      },
      {
        name: 'phone',
        type: 'tel',
        label: 'auth.phone', // i18n key
        validation: 'required|phone',
        autocomplete: 'tel'
      },
      {
        name: 'email',
        type: 'email',
        label: 'auth.email_placeholder', // i18n key
        validation: 'required|email|no_spaces',
        autocomplete: 'email'
      },
      {
        name: 'password',
        type: 'password',
        label: 'auth.password_placeholder', // i18n key
        validation: 'required|password',
        autocomplete: 'new-password'
      },
      {
        name: 'promo',
        type: 'text',
        label: 'auth.promo_placeholder', // i18n key
        validation: '',
        autocomplete: 'off'
      }
    ],
    submitText: 'auth.signup.button', // i18n key
    checkboxes: [
      {
        name: 'terms',
        label: 'auth.terms', // i18n key
        linkText: 'auth.termslink', // i18n key
        linkUrl: 'https://www.mashup-web.com/mentions-legales-cgs',
        validation: 'required',
        required: true
      },
      {
        name: 'trial',
        label: 'auth.trial', // i18n key (+ auth.trial.promo for promo)
        defaultValue: true
      }
    ]
  }
}

// Regex patterns for reuse
const REGEX_PATTERNS = {
  PHONE: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
  EMAIL: /^[\w-.+]+@([\w-]+\.)+[\w-]{2,10}$/,
  NO_SPACES: /^\S*$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?]).{8,}$/
}

/**
 * Get validation rules with i18n context
 * @param {Object} vueInstance - Vue component instance for $t access
 */
export const getValidationRules = vueInstance => ({
  phone: {
    getMessage(field, args) {
      return vueInstance.$t('auth.validation.phone')
    },
    validate(value, args) {
      if (!value || typeof value !== 'string') return false
      return REGEX_PATTERNS.PHONE.test(value)
    }
  },

  email: {
    getMessage(field, args) {
      return vueInstance.$t('auth.validation.email')
    },
    validate(value, args) {
      if (!value || typeof value !== 'string') return false
      return REGEX_PATTERNS.EMAIL.test(value.trim())
    }
  },

  no_spaces: {
    getMessage(field, args) {
      return vueInstance.$t('auth.validation.no_spaces')
    },
    validate(value, args) {
      if (!value || typeof value !== 'string') return true // Empty is OK for no_spaces
      return REGEX_PATTERNS.NO_SPACES.test(value)
    }
  },

  required: {
    getMessage(field, args) {
      return vueInstance.$t('auth.validation.required')
    },
    validate(value, args) {
      // Handle boolean values (checkboxes)
      if (typeof value === 'boolean') {
        return value === true
      }
      // Handle null/undefined values
      if (value === null || value === undefined) {
        return false
      }
      // Handle string values (text inputs)
      if (typeof value === 'string') {
        return value.trim().length > 0
      }
      // Handle other types (numbers, etc.)
      return Boolean(value)
    }
  },

  password: {
    getMessage(field, args) {
      return vueInstance.$t('auth.validation.password_strength')
    },
    validate(value, args) {
      if (!value) return false
      // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character
      return REGEX_PATTERNS.PASSWORD.test(value)
    }
  }
})
